import React, { useState, useRef, useEffect } from 'react';
import './MultiSelectDropdown.css';

interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  allOptionLabel?: string;
  counts?: Record<string, number>;
  disabled?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  values,
  onChange,
  placeholder = 'Select...',
  allOptionLabel = 'All',
  counts,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  const handleToggleOption = (option: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (option === 'all') {
      // Clicking "All" clears all selections
      onChange([]);
    } else {
      // Toggle the selected option
      if (values.includes(option)) {
        onChange(values.filter(v => v !== option));
      } else {
        onChange([...values, option]);
      }
    }
  };

  const handleSelectAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange([]);
  };

  // Display text based on selection
  const getDisplayText = () => {
    if (values.length === 0) {
      return placeholder;
    } else if (values.length === 1) {
      return values[0];
    } else {
      return `${values.length} selected`;
    }
  };

  const isAllSelected = values.length === 0;

  return (
    <div className={`multi-select-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <label>{label}</label>
      <div className="dropdown-container">
        <button
          type="button"
          className={`dropdown-trigger ${disabled ? 'disabled' : ''} ${values.length > 0 ? 'has-selection' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span className={isAllSelected ? 'placeholder-text' : ''} title={isAllSelected ? undefined : getDisplayText()}>
            {getDisplayText()}
          </span>
          {disabled ? (
            <span className="dropdown-loading-indicator"></span>
          ) : (
            <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
          )}
        </button>

        {isOpen && (
          <div className="dropdown-menu">
            <div className="dropdown-search">
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div className="dropdown-options">
              <button
                type="button"
                className={`dropdown-option ${isAllSelected ? 'selected' : ''}`}
                onClick={handleSelectAll}
              >
                <span className="checkbox">{isAllSelected ? '✓' : ''}</span>
                <span className="option-label">{allOptionLabel}</span>
              </button>
              {filteredOptions.length > 0 ? (
                <>
                  {/* Selected items first */}
                  {filteredOptions.filter(o => values.includes(o)).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className="dropdown-option selected"
                      onClick={(e) => handleToggleOption(option, e)}
                    >
                      <span className="checkbox">✓</span>
                      <span className="option-label" title={option}>{option}</span>
                      {counts && counts[option] !== undefined && (
                        <span className="option-count">{counts[option].toLocaleString()}</span>
                      )}
                    </button>
                  ))}
                  {/* Divider between selected and unselected */}
                  {values.length > 0 && filteredOptions.some(o => !values.includes(o)) && (
                    <div className="dropdown-divider" />
                  )}
                  {/* Unselected items */}
                  {filteredOptions.filter(o => !values.includes(o)).slice(0, 100).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className="dropdown-option"
                      onClick={(e) => handleToggleOption(option, e)}
                    >
                      <span className="checkbox"></span>
                      <span className="option-label" title={option}>{option}</span>
                      {counts && counts[option] !== undefined && (
                        <span className="option-count">{counts[option].toLocaleString()}</span>
                      )}
                    </button>
                  ))}
                  {filteredOptions.filter(o => !values.includes(o)).length > 100 && (
                    <div className="no-options">
                      Showing 100 of {filteredOptions.filter(o => !values.includes(o)).length.toLocaleString()} results. Type to search...
                    </div>
                  )}
                </>
              ) : (
                <div className="no-options">No matches found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
